import React from "react";
import { useIntl } from "react-intl";
import { unstable_useForkRef } from "@mui/utils";
import { type DateOrTimeViewWithMeridiem, type PickerRangeValue } from "@mui/x-date-pickers/internals";
import {
	usePickerContext,
	useSplitFieldProps,
	DateRangePicker,
	renderDateRangeViewCalendar,
	type DateRangePickerFieldProps,
	type DateRangePickerProps,
	useValidation,
	validateDateRange,
} from "@mui/x-date-pickers-pro";
import type { SxProps } from "@mui/material";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";

import { DateTimeRangeBaseButton, DateTimeRangeActionBar, DateTimeRangeText } from "../components";
import { getShortcutItems, type ShortcutItem, type ShortcutItemType } from "../utils";

const DATE_FORMAT = "yyyy-MM-dd";

interface ButtonDateRangeFieldProps extends DateRangePickerFieldProps {
	readonly shortcuts: ShortcutItem[];
}

const ButtonDateRangeField: React.FC<ButtonDateRangeFieldProps> = ({ shortcuts, ...props }) => {
	const { internalProps } = useSplitFieldProps(props, "date");

	const pickerContext = usePickerContext<PickerRangeValue, DateOrTimeViewWithMeridiem, string | null>();

	const handleRef = unstable_useForkRef(pickerContext.triggerRef, pickerContext.rootRef);

	const selectedShortcut = shortcuts.find((sc) => {
		const shortcutValue = sc.getValue({ isValid: () => true });

		return (
			pickerContext.value[0]?.toFormat(DATE_FORMAT) == shortcutValue[0]?.toFormat(DATE_FORMAT) &&
			pickerContext.value[1]?.toFormat(DATE_FORMAT) == shortcutValue[1]?.toFormat(DATE_FORMAT)
		);
	});

	const { hasValidationError } = useValidation({
		validator: validateDateRange,
		value: pickerContext.value,
		timezone: pickerContext.timezone,
		props: internalProps,
	});

	return (
		<DateTimeRangeBaseButton
			disableRipple
			type="button"
			variant="outlined"
			color={hasValidationError ? "error" : "primary"}
			ref={handleRef}
			className={[pickerContext.rootClassName, pickerContext.open && "Mui-focused"].filter(Boolean).join(" ")}
			sx={pickerContext.rootSx}
			onClick={() => {
				if (pickerContext.open) {
					pickerContext.acceptValueChanges();
				} else {
					pickerContext.setOpen(true);
				}
			}}
			data-testid="date-range-picker-button"
		>
			<DateTimeRangeText shortcut={selectedShortcut} />
			<CalendarTodayOutlined sx={{ fontSize: 20 }} />
		</DateTimeRangeBaseButton>
	);
};

export interface DateRangePickerButtonProps extends DateRangePickerProps {
	readonly shortcuts?: ShortcutItemType[];
	readonly textSx?: SxProps;
}

export const DateRangePickerButton: React.FC<DateRangePickerButtonProps> = ({
	shortcuts,
	slotProps,
	timezone,
	textSx,
	...props
}) => {
	const intl = useIntl();
	const shortcutsToUse = getShortcutItems(timezone, intl).filter((s) => shortcuts?.includes(s.type));

	return (
		<DateRangePicker
			{...props}
			viewRenderers={{
				day: (rendererProps) =>
					renderDateRangeViewCalendar({
						...rendererProps,
						shouldDisableDate: (day, position) => {
							if (rendererProps.shouldDisableDate?.(day, position) === true) {
								return true;
							}

							const start = rendererProps.value?.[0];
							if (position === "end" && start !== null && start !== undefined && start.isValid) {
								return day.startOf("day") < start.startOf("day");
							}

							return false;
						},
					}),
				...props.viewRenderers,
			}}
			slotProps={{
				...slotProps,
				shortcuts: { items: shortcutsToUse, ...slotProps?.shortcuts },
				// @ts-expect-error this prop does not exist on standard field but is passed to button
				field: { shortcuts: shortcutsToUse, textSx, ...slotProps?.field },
			}}
			slots={{
				...props.slots,
				field: ButtonDateRangeField,
				actionBar: DateTimeRangeActionBar,
			}}
		/>
	);
};
